import { EnterpriseSettings, LocalDataPolicy, NotetakerTranscriptRetention, SettingType } from '@/types'

export function isGranularDataControlsUiVisible(settings: EnterpriseSettings): boolean {
  return settings[SettingType.granularDataControlsUiVisible] ?? true
}

export function getAllowShareData(settings: EnterpriseSettings): boolean {
  return settings[SettingType.allowShareData] ?? !settings[SettingType.zdr]
}

export function getAllowCloudSync(settings: EnterpriseSettings): boolean {
  return settings[SettingType.allowCloudSync] ?? true
}

export function isZeroDataRetentionEnforced(settings: EnterpriseSettings): boolean {
  return !getAllowShareData(settings) && !getAllowCloudSync(settings)
}

// Severity = how much the org binding mandates deleting. Higher = more deletion.
// `never_delete` and `managed_individually` sit at the floor (no org-mandated
// deletion); the finite windows climb as they shorten. Drives the confirm gate —
// a string sort would be wrong here (`'30_days' < '7_days'`).
const RETENTION_SEVERITY: Record<NotetakerTranscriptRetention, number> = {
  [NotetakerTranscriptRetention.NeverDelete]: 0,
  [NotetakerTranscriptRetention.ManagedIndividually]: 0,
  [NotetakerTranscriptRetention.Days365]: 1,
  [NotetakerTranscriptRetention.Days180]: 2,
  [NotetakerTranscriptRetention.Days90]: 3,
  [NotetakerTranscriptRetention.Days30]: 4,
  [NotetakerTranscriptRetention.Days7]: 5,
  [NotetakerTranscriptRetention.Days1]: 6,
}

const FINITE_RETENTION_WINDOWS: ReadonlySet<NotetakerTranscriptRetention> = new Set([
  NotetakerTranscriptRetention.Days1,
  NotetakerTranscriptRetention.Days7,
  NotetakerTranscriptRetention.Days30,
  NotetakerTranscriptRetention.Days90,
  NotetakerTranscriptRetention.Days180,
  NotetakerTranscriptRetention.Days365,
])

export function isFiniteRetentionWindow(value: NotetakerTranscriptRetention): boolean {
  return FINITE_RETENTION_WINDOWS.has(value)
}

// The org policy change needs explicit confirmation only when it now mandates
// MORE deletion than before: `to` must be a real deletion window AND stricter
// than `from` (a shorter window, or `from` didn't delete at all). Lengthening a
// window, switching to never_delete, or deferring to users (managed_individually)
// never trips it — those relax the org binding.
export function retentionRequiresConfirm(
  from: NotetakerTranscriptRetention,
  to: NotetakerTranscriptRetention
): boolean {
  if (!isFiniteRetentionWindow(to)) return false
  return RETENTION_SEVERITY[to] > RETENTION_SEVERITY[from]
}

// The value the org retention dropdown should reflect. Cloud Sync off → the inert
// store-never sentinel (explicit `false` only; an absent allowCloudSync defaults
// to true via getAllowCloudSync). Otherwise the stored value, defaulting to
// managed_individually when unset (matching the backend, which treats an absent
// org value as "defer to each user"), and mapping the legacy `never` string to
// never_delete (the on-branch rename).
// Cloud Sync state is deliberately NOT consulted: the reaper honors a stored
// window regardless of it, so masking the policy here would tell admins nothing
// is stored while previously-uploaded transcripts keep being deleted.
export function getEffectiveNotetakerRetention(settings: EnterpriseSettings): NotetakerTranscriptRetention {
  const stored = settings[SettingType.notetakerTranscriptRetention]
  if (!stored) return NotetakerTranscriptRetention.ManagedIndividually
  if ((stored as string) === 'never') return NotetakerTranscriptRetention.NeverDelete
  return stored
}

// Higher = more history the org deletes from members' devices. Always store keeps everything, like Store.
const LOCAL_DATA_POLICY_SEVERITY: Record<LocalDataPolicy, number> = {
  [LocalDataPolicy.AlwaysStore]: 0,
  [LocalDataPolicy.StoreNormally]: 0,
  [LocalDataPolicy.DeleteAfter24h]: 1,
  [LocalDataPolicy.NeverStore]: 2,
}

export type LocalDataPolicyConfirm = 'tighten' | 'unlock'

/** Tightening wins over unlocking: leaving the lock for a deleting option deletes regardless of member choices. */
export function localDataPolicyConfirm(from: LocalDataPolicy, to: LocalDataPolicy): LocalDataPolicyConfirm | null {
  if (to === from) return null
  if (LOCAL_DATA_POLICY_SEVERITY[to] > LOCAL_DATA_POLICY_SEVERITY[from]) return 'tighten'
  if (from === LocalDataPolicy.AlwaysStore) return 'unlock'
  return null
}
